import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from '@mui/material';
import style from './confirmDetails.module.scss';
import fileIcon from '../../../Assets/images/file.svg';
import closeIcon from '../../../Assets/images/close.svg';
import upload from '../../../Assets/images/bootstrap-upload.png';
import { CV_FILE_ALLOWED_FORMATS, MAX_CV_FILE_SIZE, validationMessages } from '../../../utils/constants';
import ValidationErrorLabel from '../../common/ValidationErrorLabel/ValidationErrorLabel';
import colors from '../../../scss/colors';
import { getQuery } from '../../../utils/query';
import { useIsAuthenticated } from '@azure/msal-react';
import { fetchUserData } from '../../../actions/user';

function ConfirmDetails(props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const isAuthenticated = useIsAuthenticated();
  const dispatch = useDispatch();
  const [phone, setphone] = useState(null);
  const [Phone, setPhone] = useState('');
  const [PhoneCountryCode, setPhoneCode] = useState('');
  const [Country, setCountry] = useState('');
  const [Cv, setCv] = useState(null);
  const [cvName, setCvName] = useState('');
  const [coverLetter, setCoverLetter] = useState(null);
  const [coverLetterErrors, setCoverLetterErrors] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [certificateErrors, setCertificateErrors] = useState([]);
  const initialErrorValidationValue = {
    phone: false,
    cv: false
  };
  const userId = localStorage.getItem('UserId');
  const [errors, setErrors] = useState([]);
  const [phoneReqError, setPhoneReqError] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const [cvReq, setCvReq] = useState(false);
  const [errorValidation, setErrorValidation] = useState(initialErrorValidationValue);
  const { nextHandle, data, completedProfile, questions, userDataExist } = props;
  const user = useSelector((state) => state.user?.profile);

  const trQuestions = Object.values(questions).filter(Boolean);

  const { t } = useTranslation();
  const validationMessage = useMemo(() => validationMessages(t), [t]);

  const validateInputFile = (file) => {
    setCvReq(false);
    const currentErrors = [];
    if (!CV_FILE_ALLOWED_FORMATS.includes(file?.name.toLowerCase().split('.').pop()))
      currentErrors.push(validationMessage.CV_ALLOWED_FORMATS);
    if (file?.size > MAX_CV_FILE_SIZE) currentErrors.push(validationMessage.CV_ALLOWED_FILE_SIZE);
    return currentErrors;
  };

  useEffect(() => {
    if (user && (completedProfile || userDataExist)) {
      setCvName(user?.CvName);

      if (user.Phone && user.PhoneCountryCode) {
        setPhone(user.Phone);
        setPhoneCode(user.PhoneCountryCode);
        setphone(`${user.PhoneCountryCode}${user.Phone}`);

        if (user.Country) setCountry(user.Country);
      }
    }
    if (data.PhoneCountryCode && data.Phone) {
      setPhone(data.Phone);
      setPhoneCode(data.PhoneCountryCode);
      setphone(data.PhoneCountryCode + data.Phone);

      if (data.Country) setCountry(data.Country);
    }
  }, [data, user, userDataExist]);

  const replaceFile = (e) => {
    setErrors([]);
    const validationErrors = validateInputFile(e.target.files[0]);

    if (validationErrors.length) {
      setErrors(validationErrors);
      return;
    }

    setCv(e.target.files[0]);
    setCvName(e.target.files[0].name);
  };

  const getFileExtension = (fileName) => {
    return fileName?.toLowerCase().split('.').pop();
  };

  const validateFile = (file) => {
    const errors = [];
    const ext = getFileExtension(file.name);

    if (!CV_FILE_ALLOWED_FORMATS.includes(ext)) {
      errors.push(`${file.name} has an unsupported file type.`);
    }

    if (file.size > MAX_CV_FILE_SIZE) {
      errors.push(`${file.name} exceeds the maximum file size limit.`);
    }

    return errors;
  };

  const validateFiles = (files) => {
    return files.flatMap((file) => validateFile(file));
  };

  const handleCoverLetter = (e) => {
    e.preventDefault();
    const file = e.target?.files?.[0] || e.dataTransfer?.files?.[0];
    if (!file) return;

    const errors = validateFile(file);
    if (errors.length) {
      setCoverLetterErrors(errors);
      return;
    }

    setCoverLetter(file);
    setCoverLetterErrors([]);
  };

  const handleCertifications = (e) => {
    e.preventDefault();
    setCertificateErrors([]);

    const files = e.target?.files || e.dataTransfer?.files;
    if (!files || files.length === 0) return;

    const newFiles = Array.from(files);
    const combinedFiles = [...certificates, ...newFiles];

    if (combinedFiles.length > 3) {
      setCertificateErrors(['You can only upload up to 3 certificates.']);
      return;
    }

    const errors = validateFiles(newFiles);
    if (errors.length > 0) {
      setCertificateErrors(errors);
      return;
    }

    setCertificates(combinedFiles);
  };

  const onDragOver = (ev) => ev.preventDefault();

  const validate = () => {
    const validationObj = {
      ...initialErrorValidationValue
    };
    if (!Phone) {
      validationObj.phone = true;
      setPhoneReqError(validationMessage.PHONE_NUMBER_REQ);
    }
    if (Phone && (PhoneCountryCode === 'undefined' || Phone.length < 4)) {
      validationObj.phone = true;
      setPhoneError(validationMessage.PHONE_NUMBER_INVALID);
    }
    if (!Cv) {
      validationObj.cv = true;
      setCvReq(true);
    }
    return validationObj;
  };

  const handleCoverLetterUpload = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;

    const errors = validateFile(file);
    if (errors.length) {
      setCoverLetterErrors(errors);
      return;
    }

    setCoverLetter(file);
    setCoverLetterErrors([]);
  };

  const handleCertificateUpload = (e) => {
    e.preventDefault();
    setCertificateErrors([]);

    const files = e.target?.files || e.dataTransfer?.files;
    if (!files || !files.length) return;

    const newFiles = Array.from(files);
    const combinedFiles = [...certificates, ...newFiles];

    if (combinedFiles.length > 3) {
      setCertificateErrors(['You can only upload up to 3 certificates.']);
      return;
    }

    const errors = validateFiles(newFiles);
    if (errors.length > 0) {
      setCertificateErrors(errors);
      return;
    }

    setCertificates(combinedFiles);
  };


  const removeCerts = (index) => {
    setCertificates((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    setSearchParams({ ...getQuery(), pagetype: 'details' });
  }, []);

  return (
    <div className={style.mainWrapper}>
      <div className={style.mainContent}>
        <h4 className={style.header}>Confirm Details</h4>
        <h6 className={style.sub_header}>Here’s what we’ve pulled from your account.</h6>

        <label
          className={`${style.input_label} ${errorValidation.phone ? style.input_label_error : ''}`}
          htmlFor="phoneNumber"
        >
          Phone Number
          <span style={{ color: colors.RED_ERROR }}>{`${!completedProfile && !userDataExist ? '*' : ''}`}</span>
        </label>
        <div className='pb-2 w-full'>
          <PhoneInput
            inputStyle={{ width: '100%', marginBottom: '20px' }}
            value={phone}
            onChange={(newPhone, code) => {
              setPhoneReqError(false);
              setPhoneError(false);
              setphone(newPhone);
              setPhoneCode(`${code.dialCode ? '+' : ''}${code.dialCode}`);
              setPhone(`${newPhone}`.replace(new RegExp(`${code.dialCode}`), ''));
              setCountry(code.name);
            }}
            id="phoneNumber"
          />
        </div>
        {phoneError && <ValidationErrorLabel message={phoneError} />}
        {phoneReqError && <ValidationErrorLabel message={phoneReqError} />}
        <div style={{ paddingBottom: '20px' }} />
        <label
          className={`${style.input_label} ${errorValidation.cv ? style.input_label_error : ''}`}
          htmlFor="cvUpload"
        >
          {'Upload Your CV / Resume (Required to Apply)'}
          <span style={{ color: colors.RED_ERROR }}>{`${!completedProfile && !userDataExist ? '*' : ''}`}</span>
        </label>
        <Form.Control
          id="cvUpload"
          placeholder=""
          aria-label="CV Upload"
          style={{ border: 0, padding: 0, height: 0 }}
          disabled
          className={style.simple_text_field}
        />
        {cvName && (
          <div
            className={style.uploadContainer}
          >
            <div className={style.uploadedFile}>
              <div className={style.uploadedFileName}>
                <img alt="file" className={style.file_icon} src={fileIcon} />
                <span className={style.file_name}>{cvName}</span>
              </div>
              <img onClick={() => setCvName(null)} alt="file" className={style.closeIcon} src={closeIcon} />
            </div>
          </div>
        )}
      </div>
      {!cvName && (
        <div
          onDrop={replaceFile}
          onDragOver={onDragOver}
          className={style.uploadContainer}
        >
          <button className={style.uploadWrapper} style={{ border: 'none', background: 'none' }} onDragOver={onDragOver}>
            <label className={style.uploadContainerLabel} htmlFor="file_picker" style={{ cursor: 'pointer' }}>
              <img className={style.uploadeImage} src={upload} alt="file upload" />
              <div className={style.uploadText}>
                Drop files to upload or <span className={style.browseFile}>browse your files</span>
              </div>
              <input
                id="file_picker"
                type="file"
                accept=".pdf,.docx,.doc"
                onChange={replaceFile}
                style={{ display: 'none' }}
              />
            </label>
          </button>
        </div>
      )}
      {cvReq && <span className='pt-1 pb-3'><ValidationErrorLabel message={validationMessage.CV_REQUIRED} /></span>}
      {!!errors.length && errors.map((err) => <span className='pt-1 pb-3'><ValidationErrorLabel key={err} message={err} /></span>)}

      {/* Cover Letter Upload - Optional */}
      <label className={style.input_label} htmlFor="coverLetterUpload">
        Add a Cover Letter <span className={style.optional_text}>(Optional)</span>
      </label>
      <div
        onDrop={handleCoverLetterUpload}
        onDragOver={onDragOver}
        className={style.uploadContainer}
      >
        {coverLetter ? (
          <div className={style.uploadedFile}>
            <div className={style.uploadedFileName}>
              <img alt="file" className={style.file_icon} src={fileIcon} />
              <span className={style.file_name}>{coverLetter.name}</span>
            </div>
            <img onClick={() => setCoverLetter(null)} alt="file" className={style.closeIcon} src={closeIcon} />
          </div>
        ) : (
          <button className={style.uploadWrapper} style={{ border: 'none', background: 'none' }} onDragOver={onDragOver}>
            <label className={style.uploadContainerLabel} htmlFor="file_picker" style={{ cursor: 'pointer' }}>
              <img className={style.uploadeImage} src={upload} alt="file upload" />
              <div className={style.uploadText}>
                Drop files to upload or <span className={style.browseFile}>browse your files</span>
              </div>
              <input
                id="file_picker"
                type="file"
                accept=".pdf,.docx,.doc"
                onChange={handleCoverLetter}
                style={{ display: 'none' }}
              />
            </label>
          </button>
        )}
      </div>
      {!!coverLetterErrors.length && coverLetterErrors.map((err) => (
        <ValidationErrorLabel key={err} message={err} />
      ))}

      {/* Certificates Upload - Optional */}
      <div style={{ paddingBottom: '20px' }} />
      <label className={style.input_label} htmlFor="certificatesUpload">
        Add Certificates or Supporting Documents <span className={style.optional_text}>(Optional)</span>
      </label>
      <div
        onDrop={handleCertificateUpload}
        onDragOver={onDragOver}
        className={style.uploadContainer}
      >
        {certificates?.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', width: '100%' }}>
            {certificates.map((file, index) => (
              <div className={style.uploadedFile} key={index}>
                <div className={style.uploadedFileName}>
                  <img alt="file" className={style.file_icon} src={fileIcon} />
                  <span className={style.file_name}>{file.name}</span>
                </div>
                <img onClick={() => removeCerts(index)} alt="file" className={style.closeIcon} src={closeIcon} />
              </div>
            ))}
          </div>
        ) : (
          <button className={style.uploadWrapper} style={{ border: 'none', background: 'none' }} onDragOver={onDragOver}>
            <label className={style.uploadContainerLabel} htmlFor="certs" style={{ cursor: 'pointer' }}>
              <img className={style.uploadeImage} src={upload} alt="file upload" />
              <div className={style.uploadText}>
                Drop files to upload or <span className={style.browseFile}>browse your files</span>
              </div>
              <input
                id="certs"
                type="file"
                accept=".pdf,.docx,.doc"
                onChange={handleCertifications}
                style={{ display: 'none' }}
                multiple
              />
            </label>
          </button>
        )}
      </div>
      {!!certificateErrors.length && certificateErrors.map((err) => (
        <ValidationErrorLabel classes='pb-3' key={err} message={err} />
      ))}

      <button
        className={style.next_button}
        onClick={() => {
          const validationObj = validate();
          if (trQuestions.length === 0) localStorage.removeItem('OpenedJobId');
          const validationWrongValues = Object.values(validationObj).filter(Boolean);
          if (completedProfile || userDataExist || !validationWrongValues.length) {
            nextHandle({
              Phone,
              PhoneCountryCode,
              Cv,
              Country,
              CoverLetter: coverLetter,
              ...(certificates[0] && { Document1: certificates[0] }),
              ...(certificates[1] && { Document2: certificates[1] }),
              ...(certificates[2] && { Document3: certificates[2] }),
            });
          } else {
            const validationWrongEntries = Object.entries(validationObj).filter((v) => Boolean(v[1]));
            const newErrorValidationValue = {};
            validationWrongEntries.forEach((item) => {
              const [key, value] = item;
              newErrorValidationValue[key] = value;
            });
            setErrorValidation(newErrorValidationValue);
            setTimeout(() => setErrorValidation(initialErrorValidationValue), 2000);
          }
        }}
      >{`${trQuestions.length === 0 ? 'Submit' : 'Next'}`}</button>
    </div>
  );
}

export default ConfirmDetails;
